import {
  useParams as useTanStackParams,
  useRouter as useTanStackRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useMemo } from "react";
import { resolveInternalHref } from "./internal-href";

export { resolveInternalHref } from "./internal-href";

type NavigationMode = "push" | "replace";

export interface AppRouter {
  push(href: string): void;
  replace(href: string): void;
  back(): void;
  forward(): void;
  refresh(): void;
  prefetch(href: string): void;
}

function currentUrl(): string {
  return window.location.href;
}

export function useRouter(): AppRouter {
  const router = useTanStackRouter();

  return useMemo(() => {
    const navigate = (href: string, mode: NavigationMode) => {
      const internalHref = resolveInternalHref(href, currentUrl());
      if (internalHref) {
        void router.navigate({
          href: internalHref,
          replace: mode === "replace",
        });
        return;
      }

      if (mode === "replace") {
        window.location.replace(href);
      } else {
        window.location.assign(href);
      }
    };

    return {
      push: (href) => navigate(href, "push"),
      replace: (href) => navigate(href, "replace"),
      back: () => router.history.back(),
      forward: () => router.history.forward(),
      refresh: () => {
        void router.invalidate();
      },
      prefetch: (href) => {
        const internalHref = resolveInternalHref(href, currentUrl());
        if (internalHref) {
          void router.preloadRoute({ to: ".", href: internalHref });
        }
      },
    };
  }, [router]);
}

export function usePathname(): string {
  return useRouterState({ select: (state) => state.location.pathname });
}

export function useSearchParams(): URLSearchParams {
  const search = useRouterState({
    select: (state) => state.location.searchStr,
  });
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function useParams<
  TParams extends Record<string, string | string[]> = Record<string, string>,
>(): TParams {
  return useTanStackParams({ strict: false } as never) as TParams;
}
